import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import ArenaxLayout from './ArenaxLayout';
import ArenaxGameCard from './ArenaxGameCard';
import ArenaxSquadCard from './ArenaxSquadCard';
import ArenaxPostGameModal from './ArenaxPostGameModal';
import ArenaxReputationCard from './ArenaxReputationCard';
import ArenaxTrustedTeammates from './ArenaxTrustedTeammates';
import ArenaxUsualCrew from './ArenaxUsualCrew';
import ArenaxSportsIdentity from './ArenaxSportsIdentity';
import ArenaxPlayerRivalries from './ArenaxPlayerRivalries';
import { Trophy, Calendar, Sparkles, MapPin, ArrowRight, Star, Activity, Brain, Plus, Users } from 'lucide-react';

export default function ArenaxPlayerDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [games, setGames] = useState([]);
  const [squads, setSquads] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [pendingPostGames, setPendingPostGames] = useState([]);
  const [postGameActive, setPostGameActive] = useState(null);

  const loadPending = () => {
    api.get('/arenax/player/pending-post-game').then(r => setPendingPostGames(r.data.pending || [])).catch(() => {});
  };

  useEffect(() => {
    api.get('/arenax/player/profile').then(r => setProfile(r.data)).catch(() => {});
    api.get('/arenax/player/bookings').then(r => setBookings(r.data.bookings || [])).catch(() => {});
    api.get('/arenax/player/games').then(r => setGames(r.data.games || [])).catch(() => {});
    api.get('/arenax/player/squads').then(r => setSquads(r.data.squads || [])).catch(() => {});
    api.get('/arenax/landing').then(r => setFeatured(r.data.featured_venues || [])).catch(() => {});
    loadPending();
  }, []);

  const isOnboarded = profile?.onboarded;
  const upcomingGames = games.filter(g => !['completed', 'cancelled'].includes(g.status_derived));

  return (
    <ArenaxLayout>
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10" data-testid="arenax-player-dashboard">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <div className="text-[11px] tracking-[0.4em] text-cyan-300 font-bold mb-2">YOUR HUB</div>
            <h1 className="text-3xl sm:text-4xl font-black text-slate-100">
              Welcome back, {user?.name?.split(' ')[0] || 'Player'} 🏆
            </h1>
            <p className="text-sm text-slate-400 mt-1">Your next game is just a tap away.</p>
          </div>
          <Link to="/arenax/venues" data-testid="arenax-player-cta-book">
            <button className="px-6 py-3 rounded-xl font-bold text-slate-950 text-sm flex items-center gap-2"
              style={{ background: 'var(--ax-primary-gradient)' }}>
              Book a Slot <ArrowRight className="h-4 w-4" />
            </button>
          </Link>
        </div>

        {/* Your upcoming games — Phase 2A */}
        <div className="rounded-2xl border border-amber-500/20 p-6 mb-7" style={{ background: 'linear-gradient(160deg, rgba(245,158,11,0.05), var(--ax-card-bg-soft))' }} data-testid="arenax-player-my-games">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Trophy className="h-4 w-4 text-amber-300" />
              <h2 className="text-base font-bold text-slate-100">Your upcoming games</h2>
            </div>
            <Link to="/arenax/games/host" className="text-xs text-amber-300 hover:text-amber-200 flex items-center gap-1" data-testid="arenax-player-host-cta">
              <Plus className="h-3.5 w-3.5" /> Host a game
            </Link>
          </div>
          {upcomingGames.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-sm text-slate-300 font-bold mb-1">No games scheduled.</div>
              <div className="text-xs text-slate-500 mb-4">Join one near you or host the next squad meetup.</div>
              <div className="flex items-center justify-center gap-3">
                <Link to="/arenax/games"><button className="px-4 py-2 rounded-lg text-xs font-bold text-slate-950" style={{ background: 'var(--ax-primary-gradient)' }}>Find Games</button></Link>
                <Link to="/arenax/games/host"><button className="px-4 py-2 rounded-lg text-xs font-bold text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/10">Host a Game</button></Link>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {upcomingGames.slice(0, 4).map(g => <ArenaxGameCard key={g.id} game={g} dense />)}
            </div>
          )}
        </div>

        {/* Your squads — Phase 2B */}
        <div className="rounded-2xl border border-cyan-500/20 p-6 mb-7" style={{ background: 'linear-gradient(160deg, rgba(8,145,178,0.05), var(--ax-card-bg-soft))' }} data-testid="arenax-player-my-squads">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-cyan-300" />
              <h2 className="text-base font-bold text-slate-100">Your squads</h2>
            </div>
            <Link to="/arenax/squads/create" className="text-xs text-cyan-300 hover:text-cyan-200 flex items-center gap-1" data-testid="arenax-player-create-squad-cta">
              <Plus className="h-3.5 w-3.5" /> Create squad
            </Link>
          </div>
          {squads.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-sm text-slate-300 font-bold mb-1">No squads yet.</div>
              <div className="text-xs text-slate-500 mb-4">Squads create weekly rituals. Your next match becomes your habit.</div>
              <div className="flex items-center justify-center gap-3">
                <Link to="/arenax/squads"><button className="px-4 py-2 rounded-lg text-xs font-bold text-slate-950" style={{ background: 'var(--ax-primary-gradient)' }}>Find Squads</button></Link>
                <Link to="/arenax/squads/create"><button className="px-4 py-2 rounded-lg text-xs font-bold text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/10">Create One</button></Link>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {squads.slice(0, 4).map(s => <ArenaxSquadCard key={s.id} squad={s} dense />)}
            </div>
          )}
        </div>

        {!isOnboarded && (
          <div className="rounded-2xl border border-amber-500/30 p-5 mb-7 flex items-center justify-between"
            style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.12), var(--ax-card-bg-soft))' }}
            data-testid="arenax-player-onboarding-banner">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-amber-300" />
              </div>
              <div>
                <div className="text-sm font-bold text-slate-100">Set up your sports profile</div>
                <div className="text-xs text-slate-400">Help our AI personalize venue + match recommendations.</div>
              </div>
            </div>
            <Link to="/arenax/player/onboarding"><button className="px-4 py-2 rounded-lg text-xs font-bold text-amber-300 border border-amber-500/40 hover:bg-amber-500/10">Complete now →</button></Link>
          </div>
        )}

        {/* Post-game prompt banner — Phase 2B.2 signal capture */}
        {pendingPostGames.length > 0 && (
          <div className="rounded-2xl border border-purple-500/30 p-5 mb-7 flex items-center justify-between"
            style={{ background: 'linear-gradient(135deg, rgba(168,85,247,0.12), rgba(6,182,212,0.06))' }}
            data-testid="arenax-player-postgame-banner">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-purple-500/20 border border-purple-500/40 flex items-center justify-center">
                <Star className="h-5 w-5 text-purple-300" />
              </div>
              <div>
                <div className="text-sm font-bold text-slate-100">
                  {pendingPostGames.length === 1 ? 'How did your last game go?' : `${pendingPostGames.length} games waiting for your signal`}
                </div>
                <div className="text-xs text-slate-400">Rate it. Vote MVP. Helps matchmaking learn what you love.</div>
              </div>
            </div>
            <button onClick={() => setPostGameActive({ ...pendingPostGames[0], my_id: user?.id, participants_snapshot: pendingPostGames[0].participants || [] })}
              data-testid="arenax-player-postgame-rate"
              className="px-4 py-2 rounded-lg text-xs font-bold text-slate-950"
              style={{ background: 'linear-gradient(135deg, #a855f7, #06b6d4)' }}>
              Rate the game →
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-10">
          <KpiCard icon={Calendar} label="Active Bookings" value={bookings.filter(b => b.status === 'confirmed').length} color="#06b6d4" />
          <KpiCard icon={Activity} label="Total Sessions" value={bookings.length} color="#f59e0b" />
          <KpiCard icon={Star} label="Saved Venues" value={(profile?.saved_venues || []).length} color="#ec4899" />
        </div>

        {/* Phase 2D + 2G — Trust Layer + Sports Identity (compounding loops) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-7" data-testid="arenax-trust-layer">
          <div className="lg:col-span-2">
            <ArenaxReputationCard />
          </div>
          <div className="space-y-5">
            <ArenaxSportsIdentity />
            <ArenaxPlayerRivalries />
            <ArenaxUsualCrew />
            <ArenaxTrustedTeammates />
          </div>
        </div>

        {/* Recent Bookings */}
        <div className="rounded-2xl border border-slate-800 p-6 mb-7"
          style={{ background: 'linear-gradient(160deg, rgba(8,145,178,0.10), var(--ax-card-bg-soft))' }}
          data-testid="arenax-player-bookings-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-bold text-slate-100">Recent Bookings</h2>
            <Link to="/arenax/player/bookings" className="text-xs text-cyan-300 hover:text-cyan-200">View all →</Link>
          </div>
          {bookings.length === 0 ? (
            <div className="text-center py-10 text-sm text-slate-500">
              No bookings yet. <Link to="/arenax/venues" className="text-cyan-300">Find your first venue →</Link>
            </div>
          ) : (
            <div className="space-y-2">
              {bookings.slice(0, 5).map(b => (
                <div key={b.id} className="flex items-center justify-between p-3 rounded-xl bg-slate-900/40 border border-slate-800">
                  <div>
                    <div className="text-sm font-bold text-slate-100">{b.booking_code} · <span className="text-cyan-300">{b.sport}</span></div>
                    <div className="text-xs text-slate-500">{(b.slots_snapshot || []).length} slot(s) · {b.slots_snapshot?.[0]?.date}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-slate-100">₹{b.total_amount}</div>
                    <div className={`text-[10px] uppercase tracking-wider font-bold ${b.status === 'confirmed' ? 'text-emerald-400' : 'text-amber-400'}`}>{b.status?.replace('_', ' ')}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* AI Recommendations placeholder */}
        <div className="rounded-2xl border border-cyan-500/15 p-6 mb-7"
          style={{ background: 'linear-gradient(160deg, rgba(8,145,178,0.06), rgba(245,158,11,0.04))' }}>
          <div className="flex items-center gap-2 mb-3">
            <Brain className="h-4 w-4 text-cyan-400" />
            <span className="text-[11px] tracking-[0.3em] text-cyan-300 font-bold">AI MATCHMAKING · LIVE</span>
          </div>
          <div className="text-sm text-slate-300 leading-relaxed">
            Your recommendations are now powered by the composite compatibility score — see <Link to="/arenax" className="text-cyan-300 hover:text-cyan-200">"Perfect For You"</Link> on the ArenaX home for matches.
          </div>
        </div>

        {/* Post-game modal */}
        {postGameActive && (
          <ArenaxPostGameModal
            game={postGameActive}
            onClose={() => setPostGameActive(null)}
            onSubmitted={() => { setPostGameActive(null); loadPending(); }}
          />
        )}

        {/* Discover */}
        <div className="rounded-2xl border border-slate-800 p-6"
          style={{ background: 'linear-gradient(160deg, rgba(8,145,178,0.10), var(--ax-card-bg-soft))' }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-bold text-slate-100">Discover Venues</h2>
            <Link to="/arenax/venues" className="text-xs text-cyan-300 hover:text-cyan-200">See all →</Link>
          </div>
          {featured.length === 0 ? (
            <div className="text-center py-6 text-sm text-slate-500">Venues coming online soon.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {featured.slice(0, 3).map(v => (
                <Link key={v.id} to={`/arenax/venues/${v.slug || v.id}`} className="rounded-xl border border-slate-800 hover:border-cyan-500/40 overflow-hidden transition-all"
                  style={{ background: 'linear-gradient(160deg, rgba(8,145,178,0.05), var(--ax-card-bg-soft))' }}>
                  <div className="aspect-[16/9]" style={{
                    background: v.cover_image_url ? `url(${v.cover_image_url}) center/cover` : 'linear-gradient(135deg, #0e7490, #f59e0b)',
                  }} />
                  <div className="p-3">
                    <div className="text-sm font-bold text-slate-100">{v.name}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" />{v.city}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </ArenaxLayout>
  );
}

function KpiCard({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-2xl p-5 border border-slate-800"
      style={{ background: 'linear-gradient(160deg, rgba(8,145,178,0.10), var(--ax-card-bg-soft))' }}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-3.5 w-3.5" style={{ color }} />
        <span className="text-[10px] tracking-[0.25em] text-slate-500 uppercase font-semibold">{label}</span>
      </div>
      <div className="text-3xl font-black" style={{ color }}>{value}</div>
    </div>
  );
}
